import {ErrorCodes} from "../api/consts";
import dayjs from "dayjs";
import Api from "../api/SMPFetchHelper";
import store from "../store";
import {portalTimeZoneSelector} from "../store/selectors/user.selectors";

const updatableObjectImages = require.context('../assets/images/updatable-objects',true);

export function getProperty(object, path) {
    if (typeof path === 'string') {
        return object[path];
    }
    return path.reduce((obj, key) => (obj && obj[key] !== 'undefined') ? obj[key] : undefined, object);
}

export function setProperty(object, path, newValue) {
    if (typeof path === 'string' || path.length === 1) {
        object[path] = newValue;
    }
    else {
        const baseObject = getProperty(object, path.slice(0, -1));
        baseObject[path[path.length-1]] = newValue;
    }
}

export function getBaseObject(object, path) {
    if (typeof path === 'string') {
        return object;
    }
    let currentBaseObject = object;
    if (Array.isArray(path)) {
        path.forEach((node, key, arr) => {
            if (arr.length - 1 === key) {
                return currentBaseObject;
            } else {
                currentBaseObject = currentBaseObject[node];
            }
        });
    }
    return undefined;
}

export function removeKeyInObject(object, keyToRemove) {
    const {[keyToRemove]: val, ...withoutKeyToRemove} = object;
    return withoutKeyToRemove;
}

export function swapItemsInList(list, index1, index2) {
    const result = Array.from(list);
    const [removed] = result.splice(index1, 1);
    result.splice(index2, 0, removed);

    return result;
}

export function addItemToArray(array, item) {
    return array ? [...array, item] : [item];
}

export function addItemToArrayNoDuplicates(array, item) {
    if (!array) {
        return item;
    }
    if (array.includes(item)) {
        return array;
    }
    return [...array, item];
}

export function addMultipleItemsToArrayNoDuplicates(array, itemsToAddArray) {
    if (!array) {
        return itemsToAddArray;
    }
    if (!Array.isArray(itemsToAddArray)) {
        return array;
    }
    itemsToAddArray = itemsToAddArray.filter(item => !array.includes(item));
    return [...array, ...itemsToAddArray];
}

export function removeItemFromArray(array, item) {
    return array ? array.filter(elem => elem !== item) : [];
}

export function removeMultipleItemsFromArray(array, itemsToRemoveArray) {
    if (!Array.isArray(itemsToRemoveArray)) { return array }
    return array ? array.filter(elem => !itemsToRemoveArray.includes(elem)) : [];
}

export function removeItemsFromArrayByIndexes(array, indexes) {
    const arrayCopy = [...array];
    try {
        const indexCopy = [...indexes];
        indexCopy.sort((a, b) => a - b); // Sort small -> big
        for (let i = indexCopy.length - 1; i >= 0; i--) {
            arrayCopy.splice(indexCopy[i], 1);
        }
    } catch (e) {
        console.error('removeItemsFromArrayByIndexes: Error removing items from array by indexes');
    }
    return arrayCopy;
}

export function replaceItemInIndex(array, item, index) {
    if (!array) {
        return [];
    }
    const arrayCopy = [...array];
    if (index < arrayCopy.length) {
        arrayCopy[index] = item;
    }
    return arrayCopy;
}

export function arrayContainsOneOf(array, items) {
    if (!items || !Array.isArray(array)) {
        return false;
    }
    if (Array.isArray(items)) {
        for (let i = 0; i < items.length; i++) {
            if (array.includes(items[i])) {
                return true;
            }
        }
        return false;
    } else {
        return array.includes(items); // Items is not an array
    }
}

export function arrayContainsAll(array, items) {
    if (!items || !Array.isArray(array)) {
        return false;
    }
    if (Array.isArray(items)) {
        for (let i = 0; i < items.length; i++) {
            if (!array.includes(items[i])) {
                return false;
            }
        }
        return true;
    } else {
        return array.includes(items); // Items is not an array
    }
}

export function openWindow(url, actionFunction, cancelFunction) {
    const wind = window.open(url, '', 'a');
    if (wind) {
        wind.focus();
        wind.mycreator = this;
        wind.myaction = actionFunction;
        wind.mycancel = cancelFunction;
    } else {
        alert('To allow for proper operation of this configuration portal, you must disable blocking of popup windows for this site.');
    }
}

export function isEmptyDomain(domainName) {
    return !domainName || domainName.toUpperCase() === 'NONE';
}

/**
 * Build a path for API requests using a path array
 * ['portal', 'gateway', 'get'] => '/portal/gateway/get'
 * @param {Array<String>} pathArray - array of paths
 */
export function buildAPIRequestPath(pathArray) {
    let path = '';
    if (Array.isArray(pathArray)) {
        pathArray.forEach(pathNode => {
            path += '/' + pathNode
        });
    }
    return path;
}

export function getAxiosResponseErrorCode(error) {
    // On timeout, return smc timout error code
    if (error.code === 'ECONNABORTED') {
        return 'SMC_TIMEOUT_ERROR';
    }
    // If responded with code, return response code
    if (error.response && error.response.data) {
        return error.response.data.errorCode;
    }
    return undefined;
}

export function getAxiosResponseErrorData(error) {
    if (error.response && error.response.data) {
        return error.response.data.errorData;
    }
    return undefined;
}

export function getAxiosResponseStatus(resp) {
    if (resp.response) {
        return resp.response.status;
    }
    return undefined;
}

export function isAxiosAuthError(error) {
    return getAxiosResponseStatus(error) === 401;
}

export function isAxiosTimeoutError(error) {
    return error.code === 'ECONNABORTED';
}

export function getErrorCode(error) {
    if (error.response && error.response.data) {
        return error.response.data.errorCode;
    }
    else if (error.message in ErrorCodes) {
        return error.message;
    }
    return undefined;
}

export function getAxiosFilename(axiosResponse) {
    let filename;
    const contentDisposition = axiosResponse.headers['content-disposition'];
    if (contentDisposition) {
        // Extract filename from header
        filename = contentDisposition
            .split(';')
            .find(n => n.includes('filename='))
            .replace('filename=', '')
            .trim();
    }
    return filename;
}

export function enqueueFileDownload(fileData, fileName) {
    if (fileData && fileName) {
        const url = window.URL.createObjectURL(new Blob([fileData]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
    }
}

// String to array buffer
function s2ab(s) {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i=0; i!==s.length; ++i) view[i] = s.charCodeAt(i) & 0xFF;
    return buf;
}

/**
 * Excel downloads need a different function from normal downloads, because they
 * are recieved differently and are downloaded differently
 * @param {data} fileData Base64 string of excel file data
 * @param {string} fileName file name to sand to download with
 */
export function enqueueExcelDownload(fileData, fileName) {
    const bin = atob(fileData);
    const buf = s2ab(bin);
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;' });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
}

export function hasValue(param) {
    return param !== null && typeof param !== 'undefined';
}

export function isStringI18n(str) {
    return typeof str === 'string' && str.startsWith('{{') && str.endsWith('}}');
}

export function extractI18nString(str) {
    if (isStringI18n(str)) {
        return str.substring(2, str.length-2);
    } else {
        return str;
    }
}

export function dayjsWithPortalTimezone(date) {
    if (store) {
        const portalTimeZone = portalTimeZoneSelector(store.getState());
        if (portalTimeZone) {
            try {
                return dayjs.tz(date, portalTimeZone);
            } catch (e) {}
        }
    }
    // If can't attach timezone, return with local timezone
    return dayjs(date);
}

/**
 * Return a given date formated as a date string, including only date
 * @param {(Date|Number)} date Date object or UTC millis describing date to format
 * @param usePortalTimezone
 */
export function dateToString(date, usePortalTimezone=true) {
    if (!date) {
        return null;
    }
    let dateWithTimezone;
    if (usePortalTimezone) {
        dateWithTimezone = dayjsWithPortalTimezone(date);
    } else {
        dateWithTimezone = dayjs();
    }
    return dateWithTimezone.format('MMMM DD, YYYY');
}

/**
 * Return a given date formated as a time string, including only time
 * @param {(Date|Number)} date Date object or UTC millis describing date to format
 * @param {Boolean} includeSeconds If time string should include seconds or not
 * @param usePortalTimezone
 */
export function timeToString(date, includeSeconds, usePortalTimezone=true) {
    if (!date) {
        return null;
    }
    let dateWithTimezone;
    if (usePortalTimezone) {
        dateWithTimezone = dayjsWithPortalTimezone(date);
    } else {
        dateWithTimezone = dayjs();
    }
    return dateWithTimezone.format(`HH:mm${includeSeconds ? ':ss' : ''}`);
}

/**
 * Return a given date formated as a date string, including date and time
 * @param {(Date|Number)} date Date object or UTC millis describing date to format
 * @param {Boolean} includeSeconds If time string should include seconds or not
 * @param usePortalTimezone
 */
export function dateAndTimeToString(date, includeSeconds, usePortalTimezone=true) {
    if (!date) {
        return null;
    }
    let dateWithTimezone;
    if (usePortalTimezone) {
        dateWithTimezone = dayjsWithPortalTimezone(date);
    } else {
        dateWithTimezone = dayjs();
    }
    return dateWithTimezone.format(`MMMM DD, YYYY HH:mm${includeSeconds ? ':ss' : ''}`);
}

export function translateMessageWithParams(t, message, messageParams) {
    if (!typeof t === 'function' || !message) {
        return message;
    }
    if (!messageParams || !messageParams.length) {
        return t(message);
    }
    const translatedMessageParamsArray = messageParams.map(param => isStringI18n(param) ? t(extractI18nString(param)) : param);

    return t(message, translatedMessageParamsArray);
}

export function generateRandomString(len, charSet) {
    charSet = charSet || 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let randomString = '';
    for (let i = 0; i < len; i++) {
        let randomPosition = Math.floor(Math.random() * charSet.length);
        randomString += charSet.substring(randomPosition,randomPosition+1);
    }
    return randomString;
}

export function validateIpAddress(ipAddress) {
    return /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(ipAddress);
}

export function validateSubnetMask(subnetMask) {
    return /^(((255\.){3}(255|254|252|248|240|224|192|128|0+))|((255\.){2}(255|254|252|248|240|224|192|128|0+)\.0)|((255\.)(255|254|252|248|240|224|192|128|0+)(\.0+){2})|((255|254|252|248|240|224|192|128|0+)(\.0+){3}))$/.test(subnetMask);
}

export function validateMacAddress(macAddress) {
    return /^([0-9a-fA-F]{2}:){5}[0-9a-fA-F]{2}$/.test(macAddress);
}

export function validateEmailAddress(emailAddress) {
    return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(emailAddress);
}

export function validatePhoneNumber(phoneNumber){
    return /^\d{10}$/.test(phoneNumber);
}

export function isNumeric(num) {
    return !isNaN(num);
}

export function readFileContent(file) {
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
        reader.onload = event => resolve(event.target.result)
        reader.onerror = error => reject(error)
        reader.readAsText(file)
    });
}

/**
 * Opens a new tab and attempts to login using seamless login
 * @param {String} gatewayId Name of gateway
 * @param {('reachMyDevice'|'ip')} [forceType] Should tryto login using Reach My Device, if false, this will login unsing ip address
 * @param page
 */
export function doSeamlessLogin(gatewayId, forceType, page) {
    Api.gatewaySeamlessLogin(gatewayId, forceType, page)
        .then(resp => {
            const respData = resp.data.data;
            const url = respData.applianceURL;

            const params = {
                applianceName: respData.applianceName,
                applianceUser: respData.applianceUser,
                applianceTimedout: respData.applianceTimedOut,
                applianceLoginToken: respData.applianceLoginToken,
                appliancePage: respData.appliancePage,
                smpLogin: true
            }

            const form = document.createElement('form');
            form.setAttribute('method', 'post');
            form.setAttribute('action', url);

            for (let key in params) {
                if (params.hasOwnProperty(key)) {
                    const hiddenField = document.createElement('input');
                    hiddenField.setAttribute('type', 'hidden');
                    hiddenField.setAttribute('name', key);
                    hiddenField.setAttribute('value', params[key]);

                    form.appendChild(hiddenField);
                }
            }

            const win = window.open('', '_blank');
            win.document.body.appendChild(form);
            form.submit();
            form.parentNode.removeChild(form);
        })
        .catch(() => {
            alert('Access Gateway failed. Verify server is active and your user has permissions for this operation')
        });
}

export function getFilterFreeTextSearchValue(filterObject) {
    if (filterObject && filterObject.freeTextSearch) {
        return filterObject.freeTextSearch.value;
    }
    return undefined;
}

/**
 * Get UpdatableObject image by UpdatableObject id, if not found, return a default icon
 * @param {String} uoId UpdatableObject id
 * @returns {Node} image if found, or default icon
 */
 export function getUpdatableObjectImageById(uoId) {
    const iconId = uoId.toLowerCase().replace('@app/','');
    let imageSrc;
    try {
        // Try reading the image context, if it's not there, an exception will be thrown
        imageSrc = updatableObjectImages(`./${iconId}.svg`).default;
    } catch(e) {
             imageSrc = updatableObjectImages(`./cp_uo_unknown.svg`).default;
    }
    return imageSrc;
}

